// src/views/Tarjetas/FiltroProductos.jsx
import React, { useEffect, useRef, useState } from "react";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import TarjetasAutocomplete from "../../../components/autocomplete/TarjetasAutocomplete";


const PAGE_SIZE = 200;
export default function FiltroProductos({
  initial = {},
  onSubmit = () => {},
  onChange = () => {},
  onClose = () => {},
}) {
  const panelRef = useRef(null);

  // --- helper: deja solo dígitos
  const onlyDigits = (v) => String(v || "").replace(/\D+/g, "");

  // Selección efectiva (lo que realmente filtra)
  const [tarjetaId, setTarjetaId] = useState(
    initial.tarjetaId != null ? String(initial.tarjetaId) : "",
  );
  const [tarjetaNumero, setTarjetaNumero] = useState(
    initial.tarjetaNumero ?? "",
  );

  // Texto que el usuario tipea (NO filtra por sí solo)
  const [tarjetaQ, setTarjetaQ] = useState("");

  const [productoId, setProductoId] = useState(
    initial.productoId != null ? String(initial.productoId) : "",
  );

  // Autosuggest tarjeta
  const [sugOpen, setSugOpen] = useState(false);
  const [sugLoading, setSugLoading] = useState(false);
  const [sugItems, setSugItems] = useState([]);

  // Combo productos (desde backend)
  const [productos, setProductos] = useState([]);
  const [prodLoading, setProdLoading] = useState(false);

  const [error, setError] = useState("");

  // Rehidratar cuando cambia `initial`
  useEffect(() => {
    setTarjetaId(initial.tarjetaId != null ? String(initial.tarjetaId) : "");
    setTarjetaNumero(initial.tarjetaNumero ?? "");
    setProductoId(initial.productoId != null ? String(initial.productoId) : "");
    // no tocar tarjetaQ (es input temporal)
  }, [initial]);

  // Cerrar al click afuera + ESC
  useEffect(() => {
    const onDocClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) onClose();
    };
    const onEsc = (e) => e.key === "Escape" && onClose();
    document.addEventListener("mousedown", onDocClick);
    window.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      window.removeEventListener("keydown", onEsc);
    };
  }, [onClose]);

  // Cargar productos una vez (traemos muchos para no paginar el combo)
  useEffect(() => {
    let abort = false;
    (async () => {
      setProdLoading(true);
      try {
        const data = await apiFetchJson(
          apiRoutes.productoTarjeta.autocompleteUrl("producto", "", 0, PAGE_SIZE, true),
        );
        if (!abort) {
          const list = Array.isArray(data?.items) ? data.items : [];
          setProductos(list);
        }
      } catch {
        if (!abort) setProductos([]);
      } finally {
        if (!abort) setProdLoading(false);
      }
    })();
    return () => {
      abort = true;
    };
  }, []);

  // Debounce para sugerencias de tarjeta (usa tarjetaQ, no la selección)
  useEffect(() => {
    const q = (tarjetaQ || "").trim();
    if (q.length < 2) {
      setSugItems([]);
      setSugOpen(false);
      return;
    }
    let abort = false;
    const t = setTimeout(async () => {
      setSugLoading(true);
      try {
        const data = await apiFetchJson(
          apiRoutes.productoTarjeta.autocomplete2("tarjeta", q, 0, 100),
        );
        if (!abort) {
          setSugItems(Array.isArray(data?.items) ? data.items : []);
          setSugOpen(true);
        }
      } catch {
        if (!abort) {
          setSugItems([]);
          setSugOpen(false);
        }
      } finally {
        if (!abort) setSugLoading(false);
      }
    }, 250);
    return () => {
      clearTimeout(t);
      abort = true;
    };
  }, [tarjetaQ]);

  const haySeleccionValida = !!tarjetaId || !!productoId;

  const submit = (e) => {
    e.preventDefault();
    setError("");

    // Regla: exigir selección válida (chip) o combo de producto
    if (!haySeleccionValida) {
      if ((tarjetaQ || "").trim()) {
        setError(
          "Seleccioná una tarjeta de la lista (crea el chip) o elegí un producto.",
        );
      } else {
        setError("Completá al menos un filtro.");
      }
      return;
    }

    // ✅ Enviamos número normalizado: chip || input (fallback)
    const tarjetaNumeroClean = onlyDigits(tarjetaNumero || tarjetaQ);
    const prod = productos.find((p) => String(p.id) === String(productoId));

    onSubmit({
      tarjetaId: tarjetaId || "",
      tarjetaNumero: tarjetaNumeroClean || "",
      // opcional para compatibilidad si el padre usa el texto
      tarjetaQ: tarjetaQ || "",
      productoId: productoId || "",
      productoCodigo: prod?.codigo || "",
      productoDescripcion: prod?.nombre || "",
    });
    onClose();
  };

  return (
    <div className="drawer-backdrop" onClick={onClose}>
      <div
        ref={panelRef}
        className="drawer-panel overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="drawer-header">
          <h2 className="text-title">Filtros</h2>
          <button onClick={onClose} className="btn-cancel" title="Cerrar">
            &times;
          </button>
        </div>

        <form onSubmit={submit} className="drawer-body">
          {/* TARJETA con autocomplete reutilizable + chip */}
<div>
  <label className="form-label-fil">Nro. de Tarjeta</label>

  <TarjetasAutocomplete
    value={
      tarjetaId
        ? { id: tarjetaId, numero: tarjetaNumero }
        : null
    }
    tipo="numero"
    placeholder="Buscar tarjeta..."
    onChange={(tarjeta) => {
      if (!tarjeta) {
        setTarjetaId("");
        setTarjetaNumero("");
        onChange({ tarjetaId: "", tarjetaNumero: "" });
        return;
      }

      const id = tarjeta.id != null ? String(tarjeta.id) : "";
      const num = onlyDigits(tarjeta.numero);

      setTarjetaId(id);
      setTarjetaNumero(num);

      onChange({
        tarjetaId: id,
        tarjetaNumero: num,
      });

      setError("");
    }}
  />

  {/* Chip (selección efectiva) */}
  {!!tarjetaId && (
    <div className="mb-2 flex flex-wrap gap-2">
      <span className="inline-flex items-center rounded-full px-3 py-1 text-sm">
        {tarjetaNumero || `Tarjeta #${tarjetaId}`}
        <button
          type="button"
          className="ml-2"
          title="Quitar"
          onClick={() => {
            setTarjetaId("");
            setTarjetaNumero("");
            onChange({ tarjetaId: "", tarjetaNumero: "" });
            onSubmit({}); // recarga grilla inicial
          }}
        >
          ×
        </button>
      </span>
    </div>
  )}
</div>


          {/* PRODUCTO combo dinámico (value = PROD_SQ_COD) */}
          <div>
            <label className="form-label-fil" htmlFor="productoSel">
              Producto
            </label>
            <select
              id="productoSel"
              className="input"
              value={productoId}
              onChange={(e) => {
                const idSel = e.target.value; // ← es el ID (PROD_SQ_COD)
                setProductoId(idSel);
                const prod = productos.find(
                  (p) => String(p.id) === String(idSel),
                );
                onChange({
                  productoId: idSel,
                  productoCodigo: prod?.codigo || "",
                  productoDescripcion: prod?.nombre || "",
                });
                setError("");
              }}
              disabled={prodLoading}
            >
              <option value="">
                {prodLoading ? "Cargando…" : "-- Seleccione --"}
              </option>
              {productos.map((p) => (
                <option key={p.id} value={String(p.id)}>
                  {p.codigo} - {p.nombre}
                </option>
              ))}
            </select>
          </div>

          {error && <div className="text-sm text-red-600 mt-1">{error}</div>}

          <div className="text-right">
            <button
              type="submit"
              className="btn"
              disabled={!haySeleccionValida}
            >
              Aplicar Filtros
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
